import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Warehouse } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';

import SEO from '../../components/SEO';

const ServiceCaveGrenier = () => {
  const advantages = [
    'Accès aux caves et greniers difficiles',
    'Évacuation d\'objets lourds et encombrants',
    'Tri et recyclage systématique',
    'Nettoyage sommaire de l\'espace',
    'Intervention rapide sous 48-72h',
    'Devis gratuit et sans engagement'
  ];

  const spaces = [
    {
      title: 'Caves',
      description: 'Nous vidons les caves encombrées, même humides ou difficiles d\'accès, en évacuant tous types d\'objets stockés.'
    },
    {
      title: 'Greniers',
      description: 'Désencombrement complet des greniers, même avec accès par trappe ou escalier étroit.'
    },
    {
      title: 'Garages',
      description: 'Vidage de garage rempli de mobilier, outils, cartons et objets divers accumulés au fil du temps.'
    },
    {
      title: 'Boxes de stockage',
      description: 'Nous intervenons également dans les boxes de stockage et garde-meubles à vider.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras cave, grenier et garage en Savoie | DEBECO"
        description="Désencombrement de cave, grenier, garage et box de stockage en Savoie et Haute-Savoie. Tri et recyclage inclus. Devis gratuit."
        canonical="/services/cave-grenier"
        ogImage="/images/garage.jpg"
        jsonLd={[
          {"@context":"https://schema.org","@type":"Service","name":"Débarras cave, grenier et garage","description":"Désencombrement complet de caves, greniers, garages et box. Tri sélectif, recyclage et évacuation en déchetterie agréée.","provider":{"@type":"LocalBusiness","name":"DEBECO","telephone":"+33451401617","address":{"@type":"PostalAddress","addressLocality":"Annecy","postalCode":"74600","addressCountry":"FR"}},"areaServed":[{"@type":"AdministrativeArea","name":"Haute-Savoie"},{"@type":"AdministrativeArea","name":"Savoie"},{"@type":"AdministrativeArea","name":"Ain"}]},
          {"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Services","item":"https://debeco.fr/services"},{"@type":"ListItem","position":3,"name":"Débarras cave, grenier et garage"}]}
        ]}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Débarras cave, grenier & garage</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Désencombrement total de vos espaces de stockage
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/garage.jpg"
                  webpSrc="/images/garage.webp"
                  alt="Garage encombré avant intervention de débarras DEBECO, espace rempli de cartons et objets divers"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="eager"
                  data-testid="service-cave-grenier-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Warehouse className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Désencombrement espaces de stockage</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Au fil des années, les caves, greniers et garages s'encombrent d'objets dont on ne sait plus quoi faire. Meubles anciens, cartons non ouverts depuis des années, outils rouillés, matériel de sport inutilisé... Notre service de débarras vous libère de ces encombrements.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Notre équipe est équipée pour accéder aux espaces les plus difficiles et évacuer les objets les plus lourds. Nous trions tout sur place, recyclons ce qui peut l'être, et vous rendons un espace propre et vide.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Advantages */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Les avantages de notre service</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Spaces */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Les espaces que nous vidons</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {spaces.map((space, index) => (
                  <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3">{space.title}</h3>
                      <p className="text-gray-600">{space.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Process */}
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-blue-900 mb-6 text-center">Notre méthode de travail</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[
                    { step: '1', title: 'Évaluation', desc: 'Visite pour évaluer le volume' },
                    { step: '2', title: 'Tri', desc: 'Séparation des objets valorisables' },
                    { step: '3', title: 'Évacuation', desc: 'Chargement et transport' },
                    { step: '4', title: 'Recyclage', desc: 'Traitement écologique des déchets' }
                  ].map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <span className="text-xl font-bold text-white">{item.step}</span>
                      </div>
                      <h3 className="font-bold text-blue-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos autres services de débarras</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/services/maison" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras de maison complet</p>
              <p className="text-sm text-gray-600 mt-1">Du grenier à la cave, un service global</p>
            </Link>
            <Link to="/faq" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Questions fréquentes sur le débarras</p>
              <p className="text-sm text-gray-600 mt-1">Tarifs, délais et déroulement expliqués</p>
            </Link>
            <Link to="/devis" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Estimation gratuite en ligne</p>
              <p className="text-sm text-gray-600 mt-1">Obtenez un devis en quelques minutes</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin de vider un espace de stockage ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit et retrouvez de l'espace rapidement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceCaveGrenier;
